import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import {
	Box,
	LinearProgress,
	linearProgressClasses,
	Pagination as MuiPagination,
	PaginationItem,
	styled,
	TablePaginationProps,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import {
	DataGrid as MuiDataGrid,
	DataGridProps,
	GridColumnHeaders,
	GridColumnHeadersProps,
	useGridApiContext,
	useGridSelector,
	GridCallbackDetails,
	GridPaginationModel,
	GridSlots,
	GridFilterItem,
	GridFilterModel,
	GridColType,
	useGridApiRef,
	gridPageCountSelector,
	gridClasses,
	gridExpandedRowCountSelector,
	gridRenderContextColumnsSelector,
	gridRowCountSelector,
	gridRowsLoadingSelector,
	gridVisibleColumnDefinitionsSelector,
	PropsFromSlot,
	GridColDef,
} from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import ActionButton from '~components/buttons/actionButton';
import EmptyState from '~components/data/emptyState';
import { dataGridPageSizeOptions, queryParameters } from '~constants';
import { ComparisonOperatorEnum } from '~enums';

import useSearchParamsPagination from './useSearchParamsPagination';
import Dimens from '../../themes/dimens';

interface CustomDataGridProps extends DataGridProps {
	loadingSlowly?: boolean;
	/**
	 * Set this boolean to allow error overlay inside the datagrid
	 */
	error?: boolean;
	/**
	 * Optional no-data overlay title override
	 */
	noDataTitle?: string;
	/**
	 * Optional no-data overlay description override
	 */
	noDataDescription?: string;
	/**
	 * Optional error overlay title override
	 */
	errorTitle?: string;
	/**
	 * Optional error overlay description override
	 */
	errorDescription?: string;
	/**
	 * Set this as a key for browser persistency
	 */
	persistenceKey: string;
}

const filterSplitRegex = /_|=/;
const columnSignatureSeparator = '\u001f';
const columnPropertySeparator = '\u001e';
const resizeAutosizeDelayMs = 100;
// Above this many autosizeable columns the full `autosizeColumns` becomes a
// single CPU-blocking task (MUI renders *every* column to measure it). We then
// switch to measuring only the columns currently in the viewport, on demand.
const autosizeMaxColumns = 40;
const scrollAutosizeDebounceMs = 120;

/**
 * Properties of a `GridColDef` that participate in MUI DataGrid's internal
 * column state and layout. Whenever any of these change we MUST give MUI a
 * new `columns` reference so it re-creates internal state. Closures
 * (renderCell, valueGetter, valueFormatter, …) intentionally do NOT take part
 * in the signature — they're delegated through a ref so MUI keeps the same
 * column reference (and its autosized widths) but always calls the latest
 * closure from the parent.
 */
const stableColumnSignatureFields: (keyof GridColDef)[] = [
	'field',
	'headerName',
	'type',
	'width',
	'minWidth',
	'maxWidth',
	'flex',
	'align',
	'headerAlign',
	'sortable',
	'filterable',
	'hideable',
	'resizable',
	'editable',
	'disableColumnMenu',
	'disableExport',
	'disableReorder',
	'pinnable',
	'groupable',
];

const computeColumnsSignature = (columns: readonly GridColDef[]): string =>
	columns
		.map((col) =>
			stableColumnSignatureFields
				.map((key) => {
					const value = col[key];
					if (value == null) return '';
					if (typeof value === 'function') return '';
					return String(value);
				})
				.join(columnPropertySeparator)
		)
		.join(columnSignatureSeparator);

type LoadingOverlaySlotProps = PropsFromSlot<GridSlots['loadingOverlay']> & LoadingOverlayProps;
type EmptyStateOverlaySlotProps = PropsFromSlot<GridSlots['noRowsOverlay']> & {
	title?: string;
	description?: string;
};
type HideableColumnHeadersProps = GridColumnHeadersProps & {
	onColumnHeadersHiddenChange?: (hidden: boolean) => void;
};

const Pagination = ({
	page,
	onPageChange,
	className,
}: Pick<TablePaginationProps, 'page' | 'onPageChange' | 'className'>) => {
	const apiRef = useGridApiContext();
	const pageCount = useGridSelector(apiRef, gridPageCountSelector);
	const { typography, palette } = useTheme();
	const { lineHeight: lineHeightSm, ...paragraphSmNoLineHeight } = typography.paragraphSm;

	return (
		<MuiPagination
			color='primary'
			className={className}
			count={pageCount}
			page={page + 1}
			onChange={(event, newPage) => {
				onPageChange(event as React.MouseEvent<HTMLButtonElement, MouseEvent>, newPage - 1);
			}}
			renderItem={(itemProps) => (
				<PaginationItem
					{...itemProps}
					sx={{
						...paragraphSmNoLineHeight,
						color: palette.textTertiary.main,
						'&.Mui-selected': {
							backgroundColor: palette.bgBrandSolid.main,
							color: palette.textTertiary.main,
							'&:hover': {
								backgroundColor: palette.bgBrandSolid.main,
								color: palette.textTertiary.main,
							},
						},
					}}
				/>
			)}
		/>
	);
};

/**
 * Basically the MUI Datagrid component, but with added functionality like:
 * - Url parameter synchronisation
 * - Default slots components, like a toolbar and overlays
 * - Custom overridden slot components, like the pagination
 * - User datagrid configuration persistence
 * TODO: https://mui.com/x/react-data-grid/custom-columns/#date-pickers
 * @param props
 * @returns
 */
const DataGrid = ({
	persistenceKey,
	pagination,
	error = false,
	noDataTitle,
	noDataDescription,
	errorTitle,
	errorDescription,
	paginationMode = 'server',
	sortingMode = 'server',
	sortingOrder = ['desc', 'asc'],
	filterMode = 'server',
	...dataGridProps
}: CustomDataGridProps) => {
	const apiRefHook = useGridApiRef();
	const apiRef = dataGridProps.apiRef ?? apiRefHook;
	const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const autosizeRafRef = useRef<number | null>(null);
	const scrollAutosizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const lastRowsAutosizeKeyRef = useRef<string>('');
	// Holds the markKey for the in-flight autosize so that a scroll event
	// cancelling and rescheduling the RAF doesn't lose it.
	const pendingMarkKeyRef = useRef<string | undefined>(undefined);
	// Fields already content-sized in the progressive (many-columns) path, so a
	// column is measured only once — when it first scrolls into the viewport.
	const autosizedFieldsRef = useRef<Set<string>>(new Set());
	const theme = useTheme();
	const largerThanMd = useMediaQuery(theme.breakpoints.up('md'));
	const [searchParams] = useSearchParams();
	const [hideColumnHeaders, setHideColumnHeaders] = useState(false);

	// Always-current pointer to the parent's columns. Used by the delegating
	// renderCell/valueGetter/valueFormatter wrappers below so they call the
	// latest closure, even though we hand MUI a reference-stable columns array.
	const latestColumnsRef = useRef<readonly GridColDef[]>(dataGridProps.columns);
	latestColumnsRef.current = dataGridProps.columns;

	// Signature-driven columns reference.
	//
	// Why: MUI DataGrid resets internal column state (and our autosize result)
	// whenever `props.columns` gets a new reference — see
	// `useGridColumns.js` effect that compares `apiRef.current.caches.columns
	// .lastColumnsProp === props.columns`. Parents commonly hand us a fresh
	// array on every render (inline callbacks, unmemoized props), which used
	// to make every modal/dropdown re-trigger a flex re-distribution and
	// "squeeze" the columns.
	//
	// We rebuild the array only when a layout-relevant property actually
	// changes (see `stableColumnSignatureFields`). Closures are delegated via
	// `latestColumnsRef`, so behaviour stays fresh without ever forcing a
	// columns prop reference change.
	const columnsSignature = useMemo(
		() => computeColumnsSignature(dataGridProps.columns),
		[dataGridProps.columns]
	);
	const stableColumns = useMemo<GridColDef[]>(() => {
		return latestColumnsRef.current.map((col) => {
			const wrapped: GridColDef = { ...col };
			if (col.renderCell) {
				wrapped.renderCell = (params) => {
					const latest = latestColumnsRef.current.find((c) => c.field === col.field);
					return latest?.renderCell?.(params) ?? null;
				};
			}
			if (col.renderHeader) {
				wrapped.renderHeader = (params) => {
					const latest = latestColumnsRef.current.find((c) => c.field === col.field);
					return latest?.renderHeader?.(params) ?? null;
				};
			}
			if (col.valueGetter) {
				wrapped.valueGetter = ((value, row, column, gridApiRef) => {
					const latest = latestColumnsRef.current.find((c) => c.field === col.field);
					return latest?.valueGetter?.(value, row, column, gridApiRef);
				}) as GridColDef['valueGetter'];
			}
			if (col.valueFormatter) {
				wrapped.valueFormatter = ((value, row, column, gridApiRef) => {
					const latest = latestColumnsRef.current.find((c) => c.field === col.field);
					return (latest?.valueFormatter as GridColDef['valueFormatter'])?.(
						value,
						row,
						column,
						gridApiRef
					);
				}) as GridColDef['valueFormatter'];
			}
			return wrapped;
		});
	}, [columnsSignature]);

	const [paginationModel, setPaginationModel] = useSearchParamsPagination();
	const lastPaginationModelRef = useRef<GridPaginationModel | null>(null);
	const onPaginationModelChangeProp = dataGridProps.onPaginationModelChange;
	const paginationPage = paginationModel.page;
	const paginationPageSize = paginationModel.pageSize;
	const lastFilterModelKeyRef = useRef<string>('');
	const onFilterModelChangeProp = dataGridProps.onFilterModelChange;

	const searchQueryParam = searchParams.get(queryParameters.SearchQuery);
	const filterParams = searchParams.getAll(queryParameters.Filter);
	const urlFilterModel: GridFilterModel = useMemo(
		() => ({
			items: filterParams.map((filterParam) => {
				const elements = filterParam.split(filterSplitRegex);
				const columnType = stableColumns.find((col) => col.field === elements[0])?.type ?? 'string';
				return mapFromQueryParamsToFilterModel(filterParam, columnType);
			}),
			quickFilterValues: searchQueryParam ? [searchQueryParam] : [],
		}),
		[stableColumns, filterParams, searchQueryParam]
	);
	const effectiveFilterModel = dataGridProps.filterModel ?? urlFilterModel;
	const autoSizeOptions = useMemo(() => {
		return {
			disableColumnVirtualization: true,
			includeHeaders: true,
			includeOutliers: true,
			outliersFactor: 1,
			expand: true,
		};
	}, []);
	const autoSizeableColumnsKey = useMemo(
		() =>
			stableColumns
				.filter((col: GridColDef) => {
					return col.maxWidth == undefined;
				})
				.map((col: GridColDef) => {
					return [
						col.field,
						col.headerName ?? '',
						col.minWidth ?? '',
						col.maxWidth ?? '',
						col.width ?? '',
						col.flex ?? '',
					].join(columnPropertySeparator);
				})
				.join(columnSignatureSeparator),
		[stableColumns]
	);
	const autoSizeableColumns = useMemo(
		() =>
			autoSizeableColumnsKey.length > 0 ?
				autoSizeableColumnsKey
					.split(columnSignatureSeparator)
					.map((columnSignature) => columnSignature.split(columnPropertySeparator)[0])
			:	[],
		[autoSizeableColumnsKey]
	);
	const autoSizeableColumnsSet = useMemo(() => new Set(autoSizeableColumns), [autoSizeableColumns]);
	// With many columns the full autosize is a CPU-blocking batch (see
	// `autosizeMaxColumns`). Switch to the progressive, viewport-only path.
	const isManyColumns = autoSizeableColumns.length > autosizeMaxColumns;
	const isLoading = dataGridProps.loading === true;
	const rows = dataGridProps.rows ?? [];
	const getRowId = dataGridProps.getRowId;
	const rowsAutosizeKey =
		rows.length > 0 ?
			[
				rows.length,
				getRowId?.(rows[0]) ?? rows[0]?.id ?? '',
				getRowId?.(rows[rows.length - 1]) ?? rows[rows.length - 1]?.id ?? '',
			].join(columnPropertySeparator)
		:	'';
	// Hide the unsized (flex-width) cells behind the loader until the first
	// viewport measurement has run, so the user never sees columns snap from
	// squeezed to content width after the rows load. Only for the progressive
	// (many-columns) path — small grids autosize fast and are left untouched.
	const autosizeKey = `${autoSizeableColumnsKey}${columnSignatureSeparator}${rowsAutosizeKey}`;
	const autosizeKeyRef = useRef(autosizeKey);
	autosizeKeyRef.current = autosizeKey;
	const [measuredKey, setMeasuredKey] = useState('');
	// Once the viewport has been measured at least once we never re-hide — even
	// if data refreshes or columns change. Re-hiding while the user is scrolling
	// (background polling) is more disruptive than the brief flex→content snap.
	const [hasEverMeasured, setHasEverMeasured] = useState(false);
	const isHidingForMeasure =
		isManyColumns && !isLoading && rowsAutosizeKey.length > 0 && !hasEverMeasured;
	const autosizeColumns = useCallback(() => {
		if (isLoading || autoSizeableColumns.length <= 0) return Promise.resolve();

		// Autosize measures rendered cell widths from the DOM. If the grid is not
		// laid out yet (e.g. it lives in an inactive tab and its root has zero
		// width), the measurement collapses every column to ~0 and the columns
		// appear to vanish. Skip in that case — the columns keep their flex
		// widths, and autosize re-runs on the next resize/visibility change.
		const rootWidth = apiRef.current?.rootElementRef?.current?.clientWidth ?? 0;
		if (rootWidth <= 0) return Promise.resolve();

		return (
			apiRef.current?.autosizeColumns({
				columns: autoSizeableColumns,
				...autoSizeOptions,
			}) ?? Promise.resolve()
		);
	}, [apiRef, autoSizeOptions, autoSizeableColumns, isLoading]);

	// Progressive autosize for grids with very many columns. Instead of the full
	// `autosizeColumns` (which forces *every* column to render so it can be
	// measured — a single CPU-blocking task), we measure only the columns
	// currently in the viewport, with column virtualization left ON
	// (`disableColumnVirtualization: false`), so the cost scales with the handful
	// of visible columns. Each field is measured once; the rest stay at their
	// flex width until they first scroll into view.
	const autosizeVisibleColumns = useCallback(() => {
		if (isLoading) return Promise.resolve();

		const rootWidth = apiRef.current?.rootElementRef?.current?.clientWidth ?? 0;
		if (rootWidth <= 0) return Promise.resolve();

		const { firstColumnIndex, lastColumnIndex } = gridRenderContextColumnsSelector(apiRef);
		const visibleColumns = gridVisibleColumnDefinitionsSelector(apiRef);
		const fieldsToMeasure = visibleColumns
			.slice(firstColumnIndex, lastColumnIndex + 1)
			.map((col) => col.field)
			.filter(
				(field) =>
					autoSizeableColumnsSet.has(field) && !autosizedFieldsRef.current.has(field)
			);

		if (fieldsToMeasure.length === 0) return Promise.resolve();

		fieldsToMeasure.forEach((field) => autosizedFieldsRef.current.add(field));
		return (
			apiRef.current?.autosizeColumns({
				columns: fieldsToMeasure,
				disableColumnVirtualization: false,
				includeHeaders: true,
				includeOutliers: true,
				outliersFactor: 1,
				expand: false,
			}) ?? Promise.resolve()
		);
	}, [apiRef, autoSizeableColumnsSet, isLoading]);

	// Defer autosize to after the browser has painted the new column layout.
	// MUI virtualises rows and renders the new cells in a later commit, so
	// calling autosizeColumns synchronously (e.g. from a layout effect right
	// after the columns prop changed) measures the *old/empty* DOM and squeezes
	// the columns. Two rAFs guarantee a full frame has been committed and
	// painted before we measure.
	const scheduleAutosize = useCallback(
		(markKey?: string) => {
			if (autosizeRafRef.current != null) {
				cancelAnimationFrame(autosizeRafRef.current);
			}
			// Persist the markKey across RAF cancellations: a scroll event can cancel
			// and reschedule the RAF without a markKey, which would otherwise drop the
			// key and prevent setMeasuredKey from ever being called.
			if (markKey != null) {
				pendingMarkKeyRef.current = markKey;
			}

			autosizeRafRef.current = requestAnimationFrame(() => {
				autosizeRafRef.current = requestAnimationFrame(() => {
					autosizeRafRef.current = null;

					const rootWidth = apiRef.current?.rootElementRef?.current?.clientWidth ?? 0;
					if (rootWidth <= 0) return;

					const resolvedKey = pendingMarkKeyRef.current;
					const measurement = isManyColumns ? autosizeVisibleColumns() : autosizeColumns();
					if (resolvedKey != null) {
						pendingMarkKeyRef.current = undefined;
						lastRowsAutosizeKeyRef.current = resolvedKey;
						// Reveal the (initially hidden) grid once the measured widths have
						// been applied and painted — one extra frame after the measurement.
						void measurement.then(() => {
							// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- deferred via promise + rAF after measurement, not a synchronous effect update
							requestAnimationFrame(() => {
								setMeasuredKey(resolvedKey);
								setHasEverMeasured(true);
							});
						});
					}
				});
			});
		},
		[apiRef, autosizeColumns, autosizeVisibleColumns, isManyColumns]
	);

	useEffect(
		() => () => {
			if (autosizeRafRef.current != null) {
				cancelAnimationFrame(autosizeRafRef.current);
				autosizeRafRef.current = null;
			}
		},
		[]
	);

	/**
	 * Handle a change in pagination and synchronise the changes with the url
	 * @param value
	 * @param details
	 */
	const handlePaginationModelChange = (
		model: GridPaginationModel,
		details: GridCallbackDetails
	) => {
		lastPaginationModelRef.current = model;
		onPaginationModelChangeProp?.(model, details);
		setPaginationModel(model);
	};

	useEffect(() => {
		if (!onPaginationModelChangeProp) return;

		const nextModel = { page: paginationPage, pageSize: paginationPageSize };
		const lastModel = lastPaginationModelRef.current;
		if (lastModel?.page === nextModel.page && lastModel?.pageSize === nextModel.pageSize) {
			return;
		}

		onPaginationModelChangeProp(nextModel, {} as GridCallbackDetails);
		lastPaginationModelRef.current = nextModel;
	}, [paginationPage, paginationPageSize, onPaginationModelChangeProp]);

	useEffect(() => {
		if (!onFilterModelChangeProp || dataGridProps.filterModel != null) return;

		const nextKey = serializeFilterModel(urlFilterModel);
		if (lastFilterModelKeyRef.current === nextKey) {
			return;
		}

		onFilterModelChangeProp(urlFilterModel, {} as GridCallbackDetails);
		lastFilterModelKeyRef.current = nextKey;
	}, [dataGridProps.filterModel, onFilterModelChangeProp, urlFilterModel]);

	useLayoutEffect(() => {
		if (isLoading || rowsAutosizeKey.length <= 0) return;

		const nextAutosizeKey = `${autoSizeableColumnsKey}${columnSignatureSeparator}${rowsAutosizeKey}`;
		if (lastRowsAutosizeKeyRef.current === nextAutosizeKey) {
			return;
		}

		// The column set or the underlying rows changed: previously measured widths
		// may be stale, so re-measure from scratch in the progressive path.
		autosizedFieldsRef.current = new Set();

		// The key is marked as done only once the deferred autosize actually runs
		// against a laid-out grid (see scheduleAutosize), so a run skipped due to a
		// zero-width grid is retried on the next render.
		scheduleAutosize(nextAutosizeKey);
	}, [autoSizeableColumnsKey, scheduleAutosize, isLoading, rowsAutosizeKey]);

	// We listen to window.resize (and not a ResizeObserver on the grid root) on purpose.
	// Opening a modal applies MUI's scroll lock, which mutates `body` styles and changes
	// the grid root's width without an actual viewport resize. A ResizeObserver would
	// pick that up and re-run autosize, causing the columns to "jump" to the new width.
	// `window.resize` only fires for real viewport changes, so it stays immune to modals.
	useEffect(() => {
		const handleResize = () => {
			if (resizeTimeoutRef.current !== undefined) {
				clearTimeout(resizeTimeoutRef.current);
			}

			resizeTimeoutRef.current = setTimeout(() => {
				resizeTimeoutRef.current = undefined;
				scheduleAutosize();
			}, resizeAutosizeDelayMs);
		};

		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
			if (resizeTimeoutRef.current !== undefined) {
				clearTimeout(resizeTimeoutRef.current);
				resizeTimeoutRef.current = undefined;
			}
		};
	}, [scheduleAutosize]);

	// Progressive (many-columns) path only: columns are content-sized the first
	// time they enter the viewport. Re-run the viewport measurement (debounced)
	// on horizontal scroll so freshly revealed columns snap to their width.
	useEffect(() => {
		if (!isManyColumns) return;
		const api = apiRef.current;
		if (!api?.subscribeEvent) return;

		const handleScroll = () => {
			if (scrollAutosizeTimeoutRef.current !== undefined) {
				clearTimeout(scrollAutosizeTimeoutRef.current);
			}
			scrollAutosizeTimeoutRef.current = setTimeout(() => {
				scrollAutosizeTimeoutRef.current = undefined;
				scheduleAutosize();
			}, scrollAutosizeDebounceMs);
		};

		const unsubscribe = api.subscribeEvent('scrollPositionChange', handleScroll);

		return () => {
			unsubscribe();
			if (scrollAutosizeTimeoutRef.current !== undefined) {
				clearTimeout(scrollAutosizeTimeoutRef.current);
				scrollAutosizeTimeoutRef.current = undefined;
			}
		};
	}, [isManyColumns, apiRef, scheduleAutosize]);

	// Safety net: never leave the grid stuck behind the measure loader (e.g. if the
	// deferred autosize keeps getting skipped because the grid has zero width).
	// We read `autosizeKey` via a ref so the timeout is NOT reset every time
	// `autosizeKey` changes (which would happen on every polling re-render and
	// prevent the 3 s fallback from ever firing).
	useEffect(() => {
		if (!isHidingForMeasure) return;
		const timeout = setTimeout(() => {
			setMeasuredKey(autosizeKeyRef.current);
			setHasEverMeasured(true);
		}, 3000);
		return () => clearTimeout(timeout);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- autosizeKey read via ref intentionally
	}, [isHidingForMeasure]);

	// Take notice about the order of the injected props!
	// Everything before e.g. {...rest} can be overwritten by a child component.
	// Everything after {...rest} will not be overwritten. Additions from the child should
	// be handled in a different way.
	const loadingOverlaySlotProps = {
		variant: 'linear-progress',
		...dataGridProps.slotProps?.loadingOverlay,
	} as LoadingOverlaySlotProps;

	const basePaginationSlotProps = {
		...dataGridProps.slotProps?.basePagination,
		material: {
			ActionsComponent: largerThanMd ? Pagination : undefined,
			...(dataGridProps.slotProps?.basePagination as { material?: object } | undefined)?.material,
		},
	};

	const noRowsOverlaySlotProps = {
		title: error ? errorTitle : noDataTitle,
		description: error ? errorDescription : noDataDescription,
		...(dataGridProps.slotProps?.noRowsOverlay as object),
	} as EmptyStateOverlaySlotProps;

	const noResultsOverlaySlotProps = {
		title: error ? errorTitle : noDataTitle,
		description: error ? errorDescription : noDataDescription,
		...(dataGridProps.slotProps?.noResultsOverlay as object),
	} as EmptyStateOverlaySlotProps;

	const columnHeadersSlotProps = {
		...(dataGridProps.slotProps?.columnHeaders as object),
		onColumnHeadersHiddenChange: setHideColumnHeaders,
	} as unknown as GridColumnHeadersProps;

	return (
		<StyledDataGrid
			apiRef={apiRef}
			pageSizeOptions={dataGridPageSizeOptions}
			{...dataGridProps}
			columns={stableColumns}
			paginationMode={paginationMode}
			pagination={pagination}
			paginationModel={paginationModel}
			onPaginationModelChange={handlePaginationModelChange}
			sortingMode={sortingMode}
			sortingOrder={sortingOrder}
			filterMode={filterMode}
			filterModel={effectiveFilterModel}
			columnHeaderHeight={hideColumnHeaders ? 0 : dataGridProps.columnHeaderHeight}
			// onFilterModelChange={handleFilterModelChange}
			disableColumnResize={true}
			disableAutosize={true}
			slots={{
				loadingOverlay: LoadingOverlay,
				noResultsOverlay: error ? ErrorOverlay : NoResultsOverlay,
				noRowsOverlay: error ? ErrorOverlay : NoResultsOverlay,
				...dataGridProps.slots,
				columnHeaders: HideableColumnHeaders,
			}}
			slotProps={{
				...dataGridProps.slotProps,
				loadingOverlay: loadingOverlaySlotProps,
				basePagination: basePaginationSlotProps,
				columnHeaders: columnHeadersSlotProps,
				noRowsOverlay: noRowsOverlaySlotProps,
				noResultsOverlay: noResultsOverlaySlotProps,
			}}
			// Keep the loader up until the progressive grid has measured its initial
			// viewport, and hide the still-unsized header/cells underneath it so they
			// don't flash at flex width before snapping to content. We hide the inner
			// content (not the root) so the loading overlay itself stays visible, and
			// the cells remain laid out for autosize to measure.
			loading={isLoading || isHidingForMeasure}
			sx={[
				...(Array.isArray(dataGridProps.sx) ? dataGridProps.sx
				: dataGridProps.sx != null ? [dataGridProps.sx]
				: []),
				isHidingForMeasure && {
					[`& .${gridClasses.columnHeaders}`]: { opacity: 0 },
					[`& .${gridClasses.virtualScroller}`]: { opacity: 0 },
				},
				hideColumnHeaders && {
					'--DataGrid-headersTotalHeight': '0px',
					[`& .${gridClasses.columnHeaders}`]: { display: 'none' },
				},
			]}
		/>
	);
};

const StyledDataGrid = styled(MuiDataGrid)(({ theme }) => {
	const { lineHeight: lineHeightSm, ...paragraphSmNoLineHeight } = theme.typography.paragraphSm;
	const { lineHeight, ...paragraphNoLineHeight } = theme.typography.paragraph;

	return {
		minHeight: '400px',
		width: '100%',
		// Keep horizontal scrolling available for every grid: when the columns are
		// wider than the container the grid must scroll sideways instead of being
		// clipped. Previously only some grids set this per-instance.
		overflowX: 'auto',
		borderRadius: Dimens.radiusMd,
		borderColor: theme.palette.borderTertiary.main,
		[`& .${gridClasses.cell}`]: {
			...paragraphNoLineHeight,
			color: theme.palette.textTertiary.main,
			borderColor: theme.palette.borderTertiary.main,
			[`&:focus`]: {
				outline: '1px solid ' + theme.palette.borderPrimary.main,
			},
		},
		// filler top border
		[`& .${gridClasses.filler}`]: {
			'--rowBorderColor': `${theme.palette.borderTertiary.main} !important`,
		},
		// header bottom border
		[`& .${gridClasses['row--borderBottom']} .${gridClasses.columnHeader}`]: {
			borderBottom: `1px solid ${theme.palette.borderTertiary.main}`,
		},
		// Border for rows
		[`& .${gridClasses.toolbar}`]: {
			borderColor: theme.palette.borderTertiary.main,
		},
		[`& .${gridClasses.footerContainer}`]: {
			borderColor: theme.palette.borderTertiary.main,
		},
		[`& .${gridClasses.columnHeader}`]: {
			...paragraphSmNoLineHeight,
			color: theme.palette.textTertiary.main,
			background: theme.palette.bgSecondary.main,
			borderColor: theme.palette.borderTertiary.main,
		},
		[`& .${gridClasses.columnHeaderTitle}`]: {
			...paragraphSmNoLineHeight,
			color: theme.palette.textTertiary.main,
		},
		[`& .virtual-key-row .${gridClasses.cellCheckbox}, & .virtual-key-row .${gridClasses.cellCheckbox} *`]:
			{
				cursor: 'not-allowed',
			},
		[`& .${linearProgressClasses.bar}`]: {
			height: '2px',
			backgroundColor: theme.palette.bgBrandSolid.main,
		},
		[`& .${linearProgressClasses.root}`]: {
			height: '2px',
			backgroundColor: theme.palette.bgBrandPrimary.main,
		},
	};
});

const serializeFilterModel = (model: GridFilterModel): string =>
	JSON.stringify({
		quickFilterValues: model.quickFilterValues ?? [],
		items: (model.items ?? []).map(({ field, operator, value }) => ({ field, operator, value })),
	});

const mapFromQueryParamsToFilterModel = (
	queryParamString: string,
	type: GridColType
): GridFilterItem => {
	const elements = queryParamString.split(filterSplitRegex);
	const rawValue = elements.slice(2).join('_');

	let operator;
	let value: string | null = rawValue.length > 0 ? rawValue : null;
	const isDateType = type === 'date' || type === 'dateTime';
	switch (elements[1]) {
		case ComparisonOperatorEnum.Equals:
			operator = type === 'string' ? 'equals' : 'is';
			break;
		case ComparisonOperatorEnum.NotEquals:
			operator = type === 'string' ? 'notEquals' : 'not';
			break;
		case ComparisonOperatorEnum.GreaterThan:
			operator = isDateType ? 'after' : 'greaterThan';
			break;
		case ComparisonOperatorEnum.GreaterThanOrEqual:
			operator = isDateType ? 'onOrAfter' : 'greaterThanOrEqual';
			break;
		case ComparisonOperatorEnum.LessThan:
			operator = isDateType ? 'before' : 'lessThan';
			break;
		case ComparisonOperatorEnum.LessThanOrEqual:
			operator = isDateType ? 'onOrBefore' : 'lessThanOrEqual';
			break;
		case ComparisonOperatorEnum.Contains:
			operator = 'contains';
			break;
		case ComparisonOperatorEnum.StartsWith:
			operator = 'startsWith';
			break;
		case ComparisonOperatorEnum.EndsWith:
			operator = 'endsWith';
			break;
		case ComparisonOperatorEnum.Empty:
			operator = 'isEmpty';
			value = null;
			break;
		case ComparisonOperatorEnum.NotEmpty:
			operator = 'isNotEmpty';
			value = null;
			break;
		default:
			operator = 'notEquals';
			break;
	}

	return {
		field: elements[0],
		operator: operator,
		...(value != null && { value: value }),
	};
};

const NoResultsOverlay = ({ title, description }: EmptyStateOverlaySlotProps) => {
	const { t } = useTranslation('table');
	const { palette } = useTheme();

	return (
		<Box
			sx={{
				width: '100%',
				height: '290px',
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				background: palette.bgPrimary.main,
			}}
		>
			<EmptyState
				icon={'icon/search'}
				title={title ?? t('noDataTitle', { ns: 'table' })}
				description={description ?? t('noDataDescription', { ns: 'table' })}
			/>
		</Box>
	);
};

const HideableColumnHeaders = ({
	onColumnHeadersHiddenChange,
	...props
}: HideableColumnHeadersProps) => {
	const apiRef = useGridApiContext();
	const totalRowCount = useGridSelector(apiRef, gridRowCountSelector);
	const visibleRowCount = useGridSelector(apiRef, gridExpandedRowCountSelector);
	const loading = useGridSelector(apiRef, gridRowsLoadingSelector);
	const shouldHide =
		(loading && visibleRowCount === 0) ||
		(!loading && (totalRowCount === 0 || (totalRowCount > 0 && visibleRowCount === 0)));

	useLayoutEffect(() => {
		onColumnHeadersHiddenChange?.(shouldHide);
	}, [onColumnHeadersHiddenChange, shouldHide]);

	return (
		<GridColumnHeaders
			{...props}
			aria-hidden={shouldHide ? true : props['aria-hidden']}
			style={{
				...props.style,
				...(shouldHide && { display: 'none' }),
			}}
		/>
	);
};

interface LoadingOverlayProps extends PropsFromSlot<GridSlots['loadingOverlay']> {
	message?: string;
}

const LoadingOverlay = ({ message }: LoadingOverlayProps) => (
	<Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
		<LinearProgress />
		{message != null && (
			<Box
				sx={{
					width: '100%',
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				<Typography>{message}</Typography>
			</Box>
		)}
	</Box>
);

const ErrorOverlay = ({ title, description }: EmptyStateOverlaySlotProps) => {
	const { t } = useTranslation('errors');
	const { palette } = useTheme();

	return (
		<Box
			sx={{
				width: '100%',
				height: '290px',
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				background: palette.bgPrimary.main,
				gap: Dimens.spacingLg,
			}}
		>
			<EmptyState
				icon={'icon/alert-circle'}
				title={title ?? t('failedToLoadData')}
				description={description ?? t('failedToLoadDataDescription')}
				iconBackdropColor={palette.bgErrorPrimary.main}
				iconTintColor={palette.fgErrorPrimary.main}
			/>
			<ActionButton
				label={t('tryAgain', { ns: 'actions' })}
				onClick={() => {
					window.location.reload();
				}}
			/>
		</Box>
	);
};

export default DataGrid;
